import { Card, CardContent } from '@/components/ui/card';
import { useI18n } from '@/hooks/useI18n';
import { IPaymentPlan } from '@/lib/builder/models/payment-plan.model';
import { PaymentPlanSummary } from '@/lib/builder/components/payment-plan/PaymentPlanSummary';
import { InstallmentRow } from '@/lib/builder/components/payment-plan/InstallmentRow';
import { firstPayableInstallment } from '@/lib/builder/utils/payment-plan.utils';
import { ILegalSettings, hasLegalDocuments } from '@/lib/legal/legal.model';
import { LegalAcceptance } from '@/lib/legal/LegalAcceptance';

interface SharedPaymentPlanPanelProps {
    plan: IPaymentPlan;
    /** false quando l'advisor non ha Stripe collegato: il pannello resta informativo. */
    paymentEnabled: boolean;
    isCreatingPayment: boolean;
    onPayInstallment: (installmentId: string) => void;
    formatAmount: (value: number) => string;
    formatDate: (iso: string) => string;
    /** Testi legali di chi incassa: se ce n'e' almeno uno, la rata si paga solo dopo la spunta. */
    legal?: ILegalSettings | null;
    legalAccepted?: boolean;
    onLegalAcceptedChange?: (accepted: boolean) => void;
    /** false finche' i termini non sono accettati. La spunta e' unica per pagina. */
    canPay?: boolean;
}

/**
 * Le rate viste dal cliente nell'itinerario condiviso.
 *
 * Il pulsante di pagamento compare su UNA sola rata, la prima aperta: pagare il saldo
 * prima dell'acconto non ha senso, e due CTA insieme si farebbero concorrenza.
 */
export const SharedPaymentPlanPanel = ({
    plan,
    paymentEnabled,
    isCreatingPayment,
    onPayInstallment,
    formatAmount,
    formatDate,
    legal = null,
    legalAccepted = false,
    onLegalAcceptedChange,
    canPay = true,
}: SharedPaymentPlanPanelProps) => {
    const { t } = useI18n();

    const firstOpen = firstPayableInstallment(plan);

    return (
        <Card className="w-full">
            <CardContent className="p-4 flex flex-col gap-3">
                <h5 className="text-gray-900">{t('sharedProposal.paymentPlan.title')}</h5>

                <PaymentPlanSummary plan={plan} formatAmount={formatAmount} />

                {/* La spunta dei termini precede il pulsante della rata: qui la CTA vive
                    dentro la riga, quindi il consenso va messo appena sopra l'elenco,
                    non in fondo al pannello dove si legge dopo aver gia' cliccato. */}
                {paymentEnabled && firstOpen && hasLegalDocuments(legal) && onLegalAcceptedChange && (
                    <LegalAcceptance
                        legal={legal}
                        checked={legalAccepted}
                        onCheckedChange={onLegalAcceptedChange}
                    />
                )}

                {/* Con molte rate il pannello crescerebbe oltre lo schermo: l'elenco
                    scorre al suo interno e il riepilogo resta fermo sopra. */}
                <div className="flex flex-col divide-y divide-gray-100 max-h-[min(45vh,20rem)] overflow-y-auto -mr-1 pr-1">
                    {plan.installments.map((installment) => (
                        <InstallmentRow
                            key={installment.id}
                            installment={installment}
                            isPayable={paymentEnabled && canPay && installment.id === firstOpen?.id}
                            isPaying={isCreatingPayment}
                            onPay={onPayInstallment}
                            formatAmount={formatAmount}
                            formatDate={formatDate}
                        />
                    ))}
                </div>

                {/* Chi sceglie il bonifico non torna su questa pagina: resta su Stripe con
                    l'IBAN e le istruzioni. Detto prima e' un'informazione, scoperto dopo e'
                    un pagamento che il cliente crede fallito. */}
                {paymentEnabled && firstOpen && (
                    <div className="flex flex-col gap-1">
                        {/* Un tentativo aperto non toglie il pulsante (vedi InstallmentRow):
                            va pero' detto al cliente, perche' e' l'unico che sa se il
                            bonifico l'ha davvero disposto. */}
                        {firstOpen.payment_processing && (
                            <p className="text-[11px] text-amber-700 leading-tight">
                                {t('sharedProposal.payment.retryHint')}
                            </p>
                        )}
                        <p className="text-[11px] text-gray-500 leading-tight">
                            {t('sharedProposal.payment.bankTransferNotice')}
                        </p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
